<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import logger from 'shared/helpers/logger';
import PipelinesAPI from 'dashboard/api/pipelines';
import PipelineColumn from './PipelineColumn.vue';
import PipelineHeader from './PipelineHeader.vue';
import { useStore } from 'vuex';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import PipelineCardForm from './PipelineCardForm.vue';
import Modal from '../../../../components/Modal.vue';
import PipelineCardDetails from './PipelineCardDetails.vue';
import PipelineFilter from './PipelineFilter.vue';
import { emitter } from 'shared/helpers/mitt';
import PipelineForm from './PipelineForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { PipelineBoardSkeleton } from './skeletons';
import PipelineTour from './PipelineTour.vue';
import { usePipelineTour } from 'dashboard/composables/usePipelineTour';
import LostReasonModal from './LostReasonModal.vue';
import WonReasonModal from './WonReasonModal.vue';

const props = defineProps({
  currentView: {
    type: String,
    default: 'pipeline',
  },
  pipelineCards: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['switchView', 'itemClick']);
const { t } = useI18n();
const store = useStore();

// Estado
const error = computed(() => store.state.pipelineCards.error);
const columns = ref([]);
const showAddModal = ref(false);
const selectedColumn = ref(null);
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const { canViewPipeline } = usePipelinePermissions(selectedPipeline);
const showDeleteModal = ref(false);
const itemToDelete = ref(null);
const showEditModal = ref(false);
const itemToEdit = ref(null);
const showDetailsModal = ref(false);
const itemToShow = ref(null);
// NOOVI: filtros do board vivem no store (aplicados server-side). Computed para
// manter UI e dados sempre em sincronia, mesmo após remount/troca de pipeline.
const activeFilters = computed(
  () => store.getters['pipelineCards/getActiveFilters'] || {}
);
const showFilterModal = ref(false);
const filteredResults = ref({ total: 0, stages: {} });
const isUpdating = ref(false);
const filterLoading = ref(false);
let filterTimer = null;
// Sequência de aplicação de filtros — invalida qualquer apply debounced que
// foi superado por um apply/clear mais novo (evita um timer antigo re-aplicar
// um filtro já removido).
let filterSeq = 0;
const searchQuery = ref('');
const showCreatePipelineModal = ref(false);

// Lost Reason Modal State
const showLostReasonModal = ref(false);
const pendingLostMove = ref(null); // { itemId, columnId, cardTitle }

// Won Reason Modal State
const showWonReasonModal = ref(false);
const pendingWonMove = ref(null); // { itemId, columnId, cardTitle, expectedRevenue, currency }

// Hidden Columns State
const currentUserId = computed(() => store.getters.getCurrentUserID || 0);
const currentAccountId = computed(() => store.getters.getCurrentAccountId || 0);
const hiddenColumnIds = ref(new Set());

const getColumnSettingsKey = columnId =>
  `u${currentUserId.value}_a${currentAccountId.value}_pipeline_column_${columnId}_settings`;

const initHiddenColumns = columnIds => {
  const hidden = new Set();
  columnIds.forEach(id => {
    try {
      const raw = localStorage.getItem(getColumnSettingsKey(id));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.hideColumn === true) hidden.add(id);
      }
    } catch {
      // ignore corrupt localStorage entries
    }
  });
  hiddenColumnIds.value = hidden;
};

const handleColumnHideToggle = ({ id, hidden }) => {
  const next = new Set(hiddenColumnIds.value);
  if (hidden) {
    next.add(id);
  } else {
    next.delete(id);
  }
  hiddenColumnIds.value = next;
};

const showHiddenColumn = id => {
  try {
    const key = getColumnSettingsKey(id);
    const raw = localStorage.getItem(key);
    const settings = raw ? JSON.parse(raw) : {};
    settings.hideColumn = false;
    localStorage.setItem(key, JSON.stringify(settings));
  } catch {
    // ignore
  }
  handleColumnHideToggle({ id, hidden: false });
};

// Column Reorder State (B2.1 - UX/UI Sprint)
const draggingColumnId = ref(null);
const dragOverColumnId = ref(null);
const isReorderingColumns = ref(false);

// Scroll Indicators State (Phase 1 - UX Improvements)
const scrollContainer = ref(null);
const showLeftScrollIndicator = ref(false);
const showRightScrollIndicator = ref(false);
let wheelCleanup = null;
let wheelSnapTimer = null;
let wheelAccumDelta = 0;

// Onboarding Tour (B3 - UX/UI Sprint)
const tour = usePipelineTour();

// Start tour when pipeline loads for new users
onMounted(() => {
  // Delay tour start to ensure pipeline is fully loaded
  setTimeout(() => {
    if (
      tour.shouldShowTour.value &&
      !props.isLoading &&
      selectedPipeline.value
    ) {
      tour.startTour();
    }
  }, 1500);

  // Initialize scroll indicators (Phase 1 - UX Improvements)
  nextTick(() => {
    handleScroll(); // eslint-disable-line no-use-before-define
  });
});

// Watch scrollContainer ref and add wheel listener when available
watch(
  scrollContainer,
  newContainer => {
    if (newContainer && !wheelCleanup) {
      const handleWheel = e => {
        // Se já está scrollando horizontalmente naturalmente, permitir
        if (e.deltaX !== 0) return;

        // Se o scroll vertical está disponível em modais/dropdowns, não converter
        if (e.target.closest('[role="dialog"]')) return;
        if (e.target.closest('.overflow-y-auto')) return;

        // Bug 4 (2026-04-22): handler precisa ser simétrico — antes só
        // calculava `canScrollRight`, deltaY negativo entrava no bloco
        // mesmo no início do scroll, prevent default sem feedback. Pior,
        // `wheelAccumDelta` não resetava ao inverter sentido, então o
        // snap timer arredondava com `Math.ceil` (avança pra direita)
        // mesmo quando o usuário queria voltar — daí a sensação de
        // "trava ao voltar pra esquerda". Agora ambos os lados são
        // checados; se o sinal inverte, accum reseta; e o snap usa
        // `Math.round` direção-neutro.
        const canScrollRight =
          newContainer.scrollLeft <
          newContainer.scrollWidth - newContainer.clientWidth - 5;
        const canScrollLeft = newContainer.scrollLeft > 5;
        const goingRight = e.deltaY > 0;
        const goingLeft = e.deltaY < 0;

        const blocked =
          (goingRight && !canScrollRight) || (goingLeft && !canScrollLeft);
        if (e.deltaY === 0 || blocked) return;

        e.preventDefault();
        e.stopPropagation();

        // Disable snap during wheel scroll to prevent snap-back
        newContainer.style.scrollSnapType = 'none';

        // Reset accumulator if direction inverted (evita snap pra direita
        // quando o usuário começa a voltar pra esquerda).
        if (
          (wheelAccumDelta > 0 && goingLeft) ||
          (wheelAccumDelta < 0 && goingRight)
        ) {
          wheelAccumDelta = 0;
        }
        wheelAccumDelta += e.deltaY;
        newContainer.scrollLeft += e.deltaY;
        handleScroll(); // eslint-disable-line no-use-before-define

        // After user stops scrolling, snap to nearest column
        clearTimeout(wheelSnapTimer);
        wheelSnapTimer = setTimeout(() => {
          const columnWidth = 304; // 288px column + 16px gap
          const padding = 24;
          const pos = newContainer.scrollLeft - padding;
          // Round neutro: snap pra coluna mais próxima sem favorecer
          // a direção do último delta. Antes usava ceil/floor baseado
          // no sinal de wheelAccumDelta — quebrava ao inverter o sentido.
          const colIndex = Math.round(pos / columnWidth);
          const nearest = Math.max(0, colIndex) * columnWidth + padding;
          wheelAccumDelta = 0;
          // Scroll first (with snap still disabled), then restore snap
          newContainer.scrollTo({ left: nearest, behavior: 'smooth' });
          setTimeout(() => {
            newContainer.style.scrollSnapType = '';
          }, 400);
        }, 150);
      };

      newContainer.addEventListener('wheel', handleWheel, { passive: false });

      // Store cleanup function
      wheelCleanup = () => {
        newContainer.removeEventListener('wheel', handleWheel);
      };
    }
  },
  { immediate: true }
);

// Cleanup on unmount
onUnmounted(() => {
  if (wheelCleanup) {
    wheelCleanup();
  }
  clearTimeout(filterTimer);
});

// Funções
const updateColumns = () => {
  // Obter os estágios do funil selecionado
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  if (!pipeline?.stages) return;

  // Verificar se etapas ganho/perdido estão habilitadas
  const showWonLost = pipeline.settings?.enable_won_lost_stages !== false;

  // Filtrar etapas de ganho/perdido se desabilitado
  const filteredEntries = Object.entries(pipeline.stages).filter(
    ([, stage]) => {
      if (!showWonLost) {
        return !stage.is_won_stage && !stage.is_lost_stage;
      }
      return true;
    }
  );

  // Transformar em array mantendo a ordem original do objeto
  const stagesArray = filteredEntries.map(([id, stage]) => ({
    id,
    title: stage.name || id, // Usa o id como fallback se stage.name for undefined
    color: stage.color,
    description: stage.description,
    position: parseInt(stage.position, 10) || 0,
    items: store.getters['pipelineCards/getPipelineCardItems'].filter(
      item =>
        item.pipeline_stage === id &&
        pipeline?.id &&
        item.pipeline_id &&
        Number(item.pipeline_id) === Number(pipeline.id)
    ),
  }));

  // P0.4 — logger.debug removido. As chamadas faziam .map(...) sobre
  // todos os stages a cada updateColumns, alocando arrays mesmo quando
  // o logger é no-op em produção.

  // Ordenar os estágios por posição
  stagesArray.sort((a, b) => a.position - b.position);

  // Atualizar as colunas com a ordem ordenada por posição
  columns.value = stagesArray;

  // Sync hidden column state from localStorage
  initHiddenColumns(stagesArray.map(s => s.id));
};

// Audit 2026-04-25 — watcher único (era duplicado).
// IMPORTANT: sem flush:'post'. Antes estava causando re-render duplo:
// uma vez do v-for diff de filteredColumns (com novo column.items),
// outra do post-watcher reescrevendo columns.value.
// Default flush:'pre' agrupa numa única passagem reativa.
watch(
  () => props.pipelineCards,
  () => {
    updateColumns();
  }
);

// Atualizar colunas quando o funil mudar
watch(
  selectedPipeline,
  newPipeline => {
    if (!newPipeline) return;

    // Garantir que as posições sejam números inteiros
    if (newPipeline.stages) {
      Object.values(newPipeline.stages).forEach(stage => {
        stage.position = parseInt(stage.position, 10) || 0;
      });
    }

    // Atualizar colunas com a ordenação correta
    updateColumns();
  },
  { immediate: true }
);

const handleAdd = columnId => {
  selectedColumn.value = columns.value.find(col => col.id === columnId);
  showAddModal.value = true;
};

const handleDelete = async itemToDeleteParam => {
  try {
    isUpdating.value = true;
    await store.dispatch(
      'pipelineCards/deletePipelineCardItem',
      itemToDeleteParam.id
    );
    showDeleteModal.value = false;
    itemToDelete.value = null;
  } catch (err) {
    emitter.emit('newToastMessage', {
      message: err.response?.data?.message || t('PIPELINE.ERROR_DELETING_ITEM'),
      action: { type: 'error' },
    });
  } finally {
    isUpdating.value = false;
  }
};

const handleDrop = async ({ itemId, columnId }) => {
  if (!itemId || !columnId) return;

  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  const targetStage = pipeline?.stages?.[columnId];

  // Encontrar o card para mostrar informações no modal
  const cards = store.getters['pipelineCards/getPipelineCardItems'] || [];
  const card = cards.find(c => c.id === parseInt(itemId, 10));

  // Ignorar se o card já está na mesma coluna
  if (card && card.pipeline_stage === columnId) return;

  // Verificar se a coluna de destino é etapa de "Perdido"
  if (targetStage?.is_lost_stage) {
    pendingLostMove.value = {
      itemId: parseInt(itemId, 10),
      columnId,
      cardTitle: card?.item_details?.title || card?.title || 'Item',
    };
    showLostReasonModal.value = true;
    return; // Não move ainda, espera confirmação do modal
  }

  // Verificar se a coluna de destino é etapa de "Ganho"
  if (targetStage?.is_won_stage) {
    pendingWonMove.value = {
      itemId: parseInt(itemId, 10),
      columnId,
      cardTitle: card?.item_details?.title || card?.title || 'Item',
      expectedRevenue: card?.expected_revenue || null,
      currency: card?.currency || 'BRL',
    };
    showWonReasonModal.value = true;
    return; // Não move ainda, espera confirmação do modal
  }

  // Movimento normal para outras etapas
  await performDrop(itemId, columnId); // eslint-disable-line no-use-before-define
};

// Executa o movimento do card (chamado diretamente ou após confirmação do modal)
const performDrop = async (
  itemId,
  columnId,
  { lostReason = null, wonValue = null, wonNote = null } = {}
) => {
  try {
    isUpdating.value = true;

    // Chamada ao store - o store já gerencia a atualização dos itens
    await store.dispatch('pipelineCards/moveItemToStage', {
      itemId: parseInt(itemId, 10),
      columnId,
      lostReason,
      wonValue,
      wonNote,
    });

    // Apenas forçar atualização visual das colunas sem buscar novos dados
    nextTick(() => {
      updateColumns();
    });
  } catch (err) {
    const errors = err.response?.data?.errors;
    let message =
      err.response?.data?.message || t('PIPELINE.ERROR_MOVING_ITEM');

    if (errors?.contact || errors?.contact_id) {
      message = t('PIPELINE.MOVE_ERROR_CONTACT_CROSS_TENANT');
    } else if (
      errors?.conversation_display ||
      errors?.conversation_display_id
    ) {
      message = t('PIPELINE.MOVE_ERROR_CONVERSATION_ORPHAN');
    }

    emitter.emit('newToastMessage', {
      message,
      action: { type: 'error' },
    });
  } finally {
    isUpdating.value = false;
  }
};

// Handler para confirmação do motivo de perda
const handleLostReasonConfirm = async reason => {
  showLostReasonModal.value = false;

  if (pendingLostMove.value) {
    const pending = pendingLostMove.value;
    pendingLostMove.value = null;
    await performDrop(pending.itemId, pending.columnId, {
      lostReason: reason,
    });
  }
};

// Handler para cancelamento do modal de motivo de perda
const handleLostReasonCancel = () => {
  showLostReasonModal.value = false;
  pendingLostMove.value = null;
};

// Handler para confirmação do valor de ganho
const handleWonReasonConfirm = async ({ wonValue, wonNote }) => {
  showWonReasonModal.value = false;

  if (pendingWonMove.value) {
    const pending = pendingWonMove.value;
    pendingWonMove.value = null;
    await performDrop(pending.itemId, pending.columnId, {
      wonValue,
      wonNote,
    });
  }
};

// Handler para cancelamento do modal de valor de ganho
const handleWonReasonCancel = () => {
  showWonReasonModal.value = false;
  pendingWonMove.value = null;
};

const handleSettings = () => {
  emitter.emit('newToastMessage', {
    message: t('PIPELINE.SETTINGS_COMING_SOON'),
    action: { type: 'info' },
  });
};

const handleItemCreated = async item => {
  if (!item) return;
  showAddModal.value = false;
  selectedColumn.value = null;
};

const handleSearch = query => {
  searchQuery.value = query;
};

const handleFilterClose = () => {
  showFilterModal.value = false;
};

const handleFilter = filters => {
  filterLoading.value = true;
  // Debounce — o usuário pode ajustar vários campos antes de aplicar.
  clearTimeout(filterTimer);
  filterSeq += 1;
  const seq = filterSeq;
  filterTimer = setTimeout(async () => {
    // Se um apply/clear mais novo já rodou, este timer está obsoleto.
    if (seq !== filterSeq) return;
    try {
      // Filtragem server-side: o backend devolve os cards já filtrados e o
      // watcher de props.pipelineCards reconstrói as colunas. Sem filtragem
      // em memória (que quebrava com a paginação por stage).
      await store.dispatch('pipelineCards/setActiveFilters', filters || {});
    } finally {
      await nextTick();
      filterLoading.value = false;
    }
  }, 300);
};

const handleClearFilters = async () => {
  // Cancela qualquer apply debounced pendente E o invalida via sequência —
  // senão um timer antigo re-aplicaria os filtros 300ms depois de limpar.
  clearTimeout(filterTimer);
  filterSeq += 1;
  showFilterModal.value = false;
  filterLoading.value = true;
  try {
    await store.dispatch('pipelineCards/setActiveFilters', {});
  } finally {
    await nextTick();
    filterLoading.value = false;
  }
};

const handleEdit = item => {
  itemToEdit.value = item;
  showEditModal.value = true;
};

const handleItemClick = item => {
  emit('itemClick', item);
};

const handleCardStageChanged = async ({ card }) => {
  // Replace entire card in store with updated data from API
  await store.dispatch('pipelineCards/replaceCard', card);

  // Update columns after store is updated
  await nextTick();
  updateColumns();
};

const handleEditFromDetails = item => {
  showDetailsModal.value = false;
  handleEdit(item);
};

const handleDetailsUpdate = () => {
  // No need to emit 'forceUpdate' here
};

const handleItemEdited = async item => {
  if (!item) return;
  showEditModal.value = false;
  itemToEdit.value = null;

  // Forçar atualização visual das colunas sem buscar novos dados
  nextTick(() => {
    updateColumns();
  });
};

// Handler for PipelineForm save — store already updated by PipelineForm's dispatch
const handlePipelineCreated = savedPipeline => {
  if (savedPipeline) {
    const pipelineId = savedPipeline.id || savedPipeline;
    store.dispatch('pipelines/setSelectedPipeline', {
      id: String(pipelineId),
      ...savedPipeline,
    });
  }
  showCreatePipelineModal.value = false;
};

// ========================================
// COLUMN REORDER - B2.1 UX/UI Sprint
// Drag & Drop to reorder pipeline stages
// ========================================

const handleColumnDragStart = (event, columnId) => {
  // Allow card drag to proceed without interference
  // Cards handle their own drag events
  if (event.target.closest('.pipeline-card')) {
    // Don't prevent default - let the card handle its own drag
    // Just don't set column drag state
    return;
  }

  // Only start column drag if clicking on column header or empty area
  draggingColumnId.value = columnId;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('column-id', columnId);

  // Add visual feedback class after a small delay
  requestAnimationFrame(() => {
    const columnEl = event.target.closest('.pipeline-column');
    if (columnEl) {
      columnEl.classList.add('pipeline-column--dragging');
    }
  });
};

const handleColumnDragEnd = () => {
  // Remove dragging class from all columns
  document.querySelectorAll('.pipeline-column--dragging').forEach(el => {
    el.classList.remove('pipeline-column--dragging');
  });
  document.querySelectorAll('.pipeline-column--drag-over').forEach(el => {
    el.classList.remove('pipeline-column--drag-over');
  });

  draggingColumnId.value = null;
  dragOverColumnId.value = null;
};

const handleColumnDragOver = (event, columnId) => {
  event.preventDefault();

  // Only allow column drag, not card drag
  if (!draggingColumnId.value || draggingColumnId.value === columnId) {
    return;
  }

  event.dataTransfer.dropEffect = 'move';
  dragOverColumnId.value = columnId;
};

const handleColumnDragEnter = (event, columnId) => {
  if (!draggingColumnId.value || draggingColumnId.value === columnId) {
    return;
  }

  dragOverColumnId.value = columnId;
};

const handleColumnDragLeave = event => {
  // Only clear if actually leaving the column area
  const relatedTarget = event.relatedTarget;
  if (!event.currentTarget.contains(relatedTarget)) {
    dragOverColumnId.value = null;
  }
};

const handleColumnDrop = async (event, targetColumnId) => {
  event.preventDefault();

  const sourceColumnId = draggingColumnId.value;

  // Reset drag state
  handleColumnDragEnd();

  if (!sourceColumnId || sourceColumnId === targetColumnId) {
    return;
  }

  // Find source and target indices
  const sourceIndex = columns.value.findIndex(col => col.id === sourceColumnId);
  const targetIndex = columns.value.findIndex(col => col.id === targetColumnId);

  if (sourceIndex === -1 || targetIndex === -1) {
    return;
  }

  // Optimistically reorder columns in UI — deep-clone items so concurrent
  // updateColumns() calls don't see stale position values on the same refs
  const reorderedColumns = columns.value.map(col => ({ ...col }));
  const [movedColumn] = reorderedColumns.splice(sourceIndex, 1);
  reorderedColumns.splice(targetIndex, 0, movedColumn);

  // Update positions on the cloned snapshot
  reorderedColumns.forEach((col, index) => {
    col.position = index;
  });

  columns.value = reorderedColumns;

  // Persist to backend
  await persistColumnOrder(reorderedColumns); // eslint-disable-line no-use-before-define
};

const persistColumnOrder = async reorderedColumns => {
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  if (!pipeline?.id) return;

  try {
    isReorderingColumns.value = true;

    // Build updated stages object with new positions
    const updatedStages = {};
    reorderedColumns.forEach((col, index) => {
      updatedStages[col.id] = {
        ...pipeline.stages[col.id],
        position: index,
      };
    });

    // Update via API
    await PipelinesAPI.update(pipeline.id, {
      stages: updatedStages,
    });

    // Refresh pipeline in store
    await store.dispatch('pipelines/fetch', { force: true });

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.COLUMN_REORDER.SUCCESS'),
      action: { type: 'success' },
    });
  } catch (err) {
    logger.error('Error reordering columns:', err);

    // Revert to original order
    updateColumns();

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.COLUMN_REORDER.ERROR'),
      action: { type: 'error' },
    });
  } finally {
    isReorderingColumns.value = false;
  }
};

// Scroll Indicators Handler (Phase 1 - UX Improvements)
const handleScroll = () => {
  if (!scrollContainer.value) return;

  const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value;
  // Considera o padding (24px) para não mostrar indicador quando no início real
  showLeftScrollIndicator.value = scrollLeft > 30;
  showRightScrollIndicator.value = scrollLeft < scrollWidth - clientWidth - 30;
};

// Scroll by one column (288px column + 16px gap = 304px)
const scrollRightFn = () => {
  if (!scrollContainer.value) return;
  scrollContainer.value.scrollBy({ left: 304, behavior: 'smooth' });
};

const scrollLeftFn = () => {
  if (!scrollContainer.value) return;
  scrollContainer.value.scrollBy({ left: -304, behavior: 'smooth' });
};

// Computed para filtrar os itens baseado na busca e nos filtros
const filteredColumns = computed(() => {
  // Criar uma cópia exata para manter a mesma ordem
  let result = [...columns.value];
  // P0.4 — logger.debug com .map() removido (alocava array a cada re-eval).

  // Aplicar filtros sem alterar a ordem
  if (searchQuery.value) {
    result = result.map(column => ({
      ...column,
      items: column.items.filter(item => {
        const searchTerm = searchQuery.value.toLowerCase();
        const title = (
          item.title ||
          item.item_details?.title ||
          ''
        ).toLowerCase();
        const description = (
          item.description ||
          item.item_details?.description ||
          ''
        ).toLowerCase();
        const contactName = (
          item.contact?.name ||
          item.item_details?.contact_name ||
          ''
        ).toLowerCase();
        const inboxName = (
          item.item_details?.conversation?.inbox?.name || ''
        ).toLowerCase();
        const ownerName = (item.owner?.name || '').toLowerCase();
        const conversationId = String(
          item.conversation_display_id ||
            item.item_details?.conversation?.display_id ||
            ''
        );
        return (
          title.includes(searchTerm) ||
          description.includes(searchTerm) ||
          contactName.includes(searchTerm) ||
          inboxName.includes(searchTerm) ||
          ownerName.includes(searchTerm) ||
          conversationId.includes(searchTerm)
        );
      }),
    }));
  }

  // Filter by stages if selected in global filter
  if (activeFilters.value?.stages?.length > 0) {
    result = result.filter(column =>
      activeFilters.value.stages.includes(column.id)
    );
  }

  // NOOVI: filtragem de cards (label/prioridade/valor/agente/data/status/SLA)
  // agora é server-side — o backend devolve `items` já filtrado. Aqui só
  // ocultamos colunas fora dos stages selecionados (acima) e aplicamos a busca.
  // O bloco de filtragem em memória foi removido: quebrava com a paginação por
  // stage (filtrava só a página carregada) e tinha chaves dessincronizadas.

  // Ordenar explicitamente por posição antes de retornar, garantindo ordem correta
  result.sort((a, b) => {
    const posA = parseInt(a.position, 10) || 0;
    const posB = parseInt(b.position, 10) || 0;
    return posA - posB;
  });

  // P0.4 — logger.debug com .map() removido (alocava array a cada re-eval).
  return result;
});

// Computed para detectar se há filtros ativos sem resultados
const hasFiltersWithNoResults = computed(() => {
  const f = activeFilters.value;
  if (!f) return false;

  // Verificar se há algum filtro ativo (formato aninhado do PipelineFilter).
  // value.min/max usam checagem explícita para não ignorar o valor 0.
  const isSet = v => v !== null && v !== undefined && v !== '';
  const hasActiveFilters = Boolean(
    f.labels?.length ||
      f.priority?.length ||
      f.stages?.length ||
      isSet(f.value?.min) ||
      isSet(f.value?.max) ||
      f.agent_id ||
      f.date?.start ||
      f.date?.end ||
      f.status ||
      f.sla_exceeded
  );

  if (!hasActiveFilters) return false;

  // Verificar se todas as colunas estão vazias
  const totalItems = filteredColumns.value.reduce(
    (sum, column) => sum + column.items.length,
    0
  );

  return totalItems === 0;
});

// Computed para calcular os resultados da busca
const searchResults = computed(() => {
  if (!searchQuery.value) return { total: 0, stages: {} };

  const results = {
    total: 0,
    stages: {},
  };

  filteredColumns.value.forEach(column => {
    if (column.items.length > 0) {
      results.stages[column.title] = column.items.length;
      results.total += column.items.length;
    }
  });

  return results;
});

const hiddenColumns = computed(() =>
  filteredColumns.value.filter(col => hiddenColumnIds.value.has(col.id))
);

// P0.4 — columnsOrderDebug removido. Era um computed inteiro que existia
// apenas para chamar logger.debug com .map() das colunas. Não tinha
// uso real além de log; o template referenciava num <div hidden>.

// Watch for column changes to update scroll indicators (Phase 1 - UX Improvements)
watch(
  () => filteredColumns.value,
  () => {
    nextTick(() => {
      handleScroll();
    });
  },
  { flush: 'post' }
);
</script>

<template>
  <div
    class="flex flex-col h-full bg-gradient-to-br from-n-background to-n-solid-2"
  >
    <!-- Access denied: agent is not a member of this pipeline -->
    <div
      v-if="!canViewPipeline"
      class="flex flex-1 items-center justify-center p-8"
    >
      <div class="text-center max-w-sm">
        <fluent-icon
          icon="lock-closed"
          size="40"
          class="mx-auto mb-4 text-n-slate-9"
        />
        <h3 class="text-heading-2 text-n-slate-12 mb-2">
          {{ t('PIPELINE.ACCESS_DENIED.TITLE') }}
        </h3>
        <p class="text-body-main text-n-slate-10">
          {{ t('PIPELINE.ACCESS_DENIED.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <template v-else>
      <header :aria-label="t('PIPELINE.HEADER')">
        <PipelineHeader
          :current-view="currentView"
          :current-stage="selectedColumn?.id"
          :search-results="searchResults"
          :columns="filteredColumns"
          :active-filters="activeFilters"
          :pipeline-cards="pipelineCards"
          @filter="showFilterModal = true"
          @settings="handleSettings"
          @item-created="handleItemCreated"
          @items-updated="
            () => store.dispatch('pipelineCards/fetchBoardByStages')
          "
          @search="handleSearch"
          @switch-view="view => emit('switchView', view)"
          @start-tour="
            tour.resetTour();
            tour.startTour();
          "
        />
      </header>

      <!-- Skeleton Loading State -->
      <div v-if="isLoading" class="flex-1 overflow-hidden">
        <PipelineBoardSkeleton :columns="4" :cards-per-column="[3, 2, 4, 2]" />
      </div>

      <div
        v-else-if="error"
        class="flex justify-center items-center flex-1 text-red-500"
      >
        {{ error }}
      </div>

      <div
        v-else-if="!selectedPipeline && !isLoading && !error"
        class="flex flex-col items-center justify-center flex-1 p-8 text-center"
      >
        <h3 class="text-heading-1 text-n-slate-12 mb-2">
          {{ t('PIPELINE.PIPELINES.FORM.CREATE_FIRST') }}
        </h3>
        <p class="text-body-main text-n-slate-11 max-w-md mb-6">
          {{ t('PIPELINE.PIPELINES.FORM.CREATE_FIRST_MESSAGE') }}
        </p>
        <Button
          variant="solid"
          color-scheme="woot"
          icon="add"
          :label="t('PIPELINE.PIPELINES.FORM.CREATE_BUTTON')"
          @click="showCreatePipelineModal = true"
        />
      </div>

      <main
        v-else
        :aria-label="t('PIPELINE.BOARD')"
        class="flex-1 relative flex flex-col"
      >
        <!-- Left Scroll Indicator -->
        <div
          v-if="showLeftScrollIndicator"
          class="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-n-background to-transparent cursor-pointer z-10 flex items-center justify-start pl-2"
          @click="scrollLeftFn"
        >
          <fluent-icon
            icon="chevron-left"
            size="20"
            class="text-n-slate-9 animate-pulse"
          />
        </div>

        <!-- Pipeline Columns Container -->
        <div class="flex-1 h-full pipeline-columns">
          <div
            v-if="isUpdating"
            class="absolute top-2 right-2 flex items-center text-body-main text-n-slate-10"
          >
            <span class="loading-spinner-small mr-2" />
            {{ t('PIPELINE.UPDATING') }}
          </div>

          <div
            v-if="filterLoading"
            class="absolute top-2 left-1/2 transform -translate-x-1/2 flex items-center px-3 py-2 bg-n-solid-3 rounded-xl shadow-md outline outline-1 outline-n-weak text-body-main text-n-slate-11"
          >
            <span class="loading-spinner-small mr-2" />
            {{ t('PIPELINE.FILTERS.APPLYING') }}
          </div>

          <!-- Compact banner for filters with no results - shown above columns -->
          <div
            v-if="hasFiltersWithNoResults"
            class="mx-6 mt-4 mb-2 p-3 bg-n-solid-2 border border-n-weak rounded-xl flex items-center justify-between"
          >
            <div class="flex items-center gap-3">
              <fluent-icon icon="filter" size="20" class="text-n-slate-9" />
              <div>
                <p class="text-body-main font-medium text-n-slate-12">
                  {{ t('PIPELINE.FILTERS.NO_RESULTS.TITLE') }}
                </p>
                <p class="text-label-small text-n-slate-10">
                  {{ t('PIPELINE.FILTERS.NO_RESULTS.DESCRIPTION') }}
                </p>
              </div>
            </div>
            <Button
              variant="solid"
              color="slate"
              size="sm"
              @click="handleClearFilters"
            >
              <template #icon>
                <fluent-icon icon="filter" size="14" />
              </template>
              {{ t('PIPELINE.FILTERS.NO_RESULTS.CLEAR_BUTTON') }}
            </Button>
          </div>

          <!-- Hidden Stages Banner -->
          <div
            v-if="hiddenColumns.length > 0"
            class="mx-6 mt-2 mb-1 p-3 bg-n-solid-2 border border-n-weak rounded-xl flex items-center gap-3 flex-wrap"
          >
            <div class="flex items-center gap-2 text-n-slate-10 shrink-0">
              <fluent-icon icon="eye-off" size="16" />
              <span class="text-label-small font-medium">{{
                t('PIPELINE.HIDDEN_STAGES.LABEL')
              }}</span>
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              <button
                v-for="col in hiddenColumns"
                :key="col.id"
                class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-label-small font-medium text-n-slate-11 bg-n-solid-3 hover:bg-n-alpha-2 border border-n-weak transition-colors"
                @click="showHiddenColumn(col.id)"
              >
                <span
                  class="w-2 h-2 rounded-full shrink-0"
                  :style="{ backgroundColor: col.color || '#64748b' }"
                />
                {{ col.title }}
                <fluent-icon icon="eye" size="12" class="text-n-slate-9" />
              </button>
            </div>
          </div>

          <div
            ref="scrollContainer"
            role="group"
            :aria-label="t('PIPELINE.STAGES')"
            class="pipeline-board-container flex h-full py-6 px-8 gap-4 overflow-x-auto snap-x snap-proximity scroll-pl-8 scroll-pr-8"
            @scroll="handleScroll"
          >
            <!-- P0.4 — Debug helper removido (era apenas para chamar logger). -->
            <!-- Column Reorder Wrapper (B2.1 UX/UI Sprint).
                 P0.4 — sort redundante removido do template (.slice().sort()
                 era extra; filteredColumns já ordena por posição internamente). -->
            <template v-for="column in filteredColumns" :key="column.id">
              <div
                v-if="!hiddenColumnIds.has(column.id)"
                class="pipeline-column-wrapper"
                :class="{
                  'pipeline-column--dragging': draggingColumnId === column.id,
                  'pipeline-column--drag-over':
                    dragOverColumnId === column.id &&
                    draggingColumnId !== column.id,
                }"
                draggable="true"
                @dragstart="e => handleColumnDragStart(e, column.id)"
                @dragend="handleColumnDragEnd"
                @dragover="e => handleColumnDragOver(e, column.id)"
                @dragenter="e => handleColumnDragEnter(e, column.id)"
                @dragleave="handleColumnDragLeave"
                @drop="e => handleColumnDrop(e, column.id)"
              >
                <PipelineColumn
                  :id="column.id"
                  :title="column.title"
                  :color="column.color"
                  :description="column.description"
                  :items="column.items"
                  @add="handleAdd"
                  @edit="handleEdit"
                  @delete="handleDelete"
                  @drop="handleDrop"
                  @item-click="handleItemClick"
                  @card-stage-changed="handleCardStageChanged"
                  @hide-toggle="handleColumnHideToggle"
                />
              </div>
            </template>
          </div>

          <!-- Right Scroll Indicator -->
          <div
            v-if="showRightScrollIndicator"
            class="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-n-background to-transparent cursor-pointer z-10 flex items-center justify-end pr-2"
            @click="scrollRightFn"
          >
            <fluent-icon
              icon="chevron-right"
              size="20"
              class="text-n-slate-9 animate-pulse"
            />
          </div>
        </div>
      </main>

      <!-- Modais -->
      <Modal v-model:show="showAddModal" @close="() => (showAddModal = false)">
        <div class="flex flex-col max-h-[85vh] overflow-hidden">
          <div class="px-5 pt-5 pb-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('PIPELINE.ADD_ITEM') }}
            </h3>
          </div>
          <PipelineCardForm
            v-if="selectedColumn && selectedPipeline"
            :pipeline-id="selectedPipeline.id"
            :stage="selectedColumn.id"
            :position="selectedColumn.position"
            @saved="handleItemCreated"
            @close="showAddModal = false"
          />
          <div v-else class="text-center text-red-500 p-6">
            {{ t('PIPELINE.ERRORS.NO_PIPELINE_SELECTED') }}
          </div>
        </div>
      </Modal>

      <Modal
        v-model:show="showDeleteModal"
        @close="
          () => {
            showDeleteModal = false;
            itemToDelete = null;
          }
        "
      >
        <div class="p-6">
          <h3 class="text-lg font-medium mb-4">
            {{ t('PIPELINE.DELETE_CONFIRMATION.TITLE') }}
          </h3>
          <p class="text-body-main text-n-slate-11 mb-6">
            {{
              t('PIPELINE.DELETE_CONFIRMATION.MESSAGE', {
                item: itemToDelete?.title,
              })
            }}
          </p>
          <div class="flex justify-end space-x-2">
            <button
              variant="clear"
              color-scheme="secondary"
              @click="showDeleteModal = false"
            >
              {{ t('PIPELINE.CANCEL') }}
            </button>
            <button
              variant="solid"
              color-scheme="alert"
              @click="handleDelete(itemToDelete)"
            >
              {{ t('PIPELINE.DELETE') }}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        v-model:show="showEditModal"
        @close="
          () => {
            showEditModal = false;
            itemToEdit = null;
          }
        "
      >
        <div class="flex flex-col max-h-[85vh] overflow-hidden">
          <div class="px-5 pt-5 pb-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('PIPELINE.FORM.EDIT_ITEM') }}
            </h3>
          </div>
          <PipelineCardForm
            v-if="itemToEdit && selectedPipeline"
            :pipeline-id="selectedPipeline.id"
            :stage="itemToEdit.pipeline_stage"
            :position="itemToEdit.position"
            :initial-data="itemToEdit"
            is-editing
            @saved="handleItemEdited"
            @close="showEditModal = false"
          />
        </div>
      </Modal>

      <Modal
        v-model:show="showDetailsModal"
        size="full-width"
        @close="
          () => {
            showDetailsModal = false;
            itemToShow = null;
            handleDetailsUpdate();
          }
        "
      >
        <PipelineCardDetails
          v-if="itemToShow"
          :key="itemToShow.id"
          :item="itemToShow"
          @close="
            () => {
              showDetailsModal = false;
              itemToShow = null;
              handleDetailsUpdate();
            }
          "
          @edit="handleEditFromDetails"
          @item-updated="handleDetailsUpdate"
        />
      </Modal>

      <!-- Modal for Creating Pipeline -->
      <Modal
        v-model:show="showCreatePipelineModal"
        size="large"
        @close="() => (showCreatePipelineModal = false)"
      >
        <div class="p-6">
          <h3 class="text-lg font-medium mb-6">
            {{ t('PIPELINE.PIPELINES.FORM.CREATE.TITLE') }}
          </h3>
          <PipelineForm
            @saved="handlePipelineCreated"
            @close="showCreatePipelineModal = false"
          />
        </div>
      </Modal>

      <!-- Montagem lazy: evita o dispatch de agents/labels do onMounted no
           load do board (o modal interno já é v-if="show") -->
      <PipelineFilter
        v-if="showFilterModal"
        :show="showFilterModal"
        :columns="columns"
        :filtered-results="filteredResults"
        :initial-filters="activeFilters"
        :current-pipeline="store.getters['pipelines/getSelectedPipeline']"
        @close="handleFilterClose"
        @apply="handleFilter"
      />

      <!-- Lost Reason Modal -->
      <LostReasonModal
        :show="showLostReasonModal"
        :card-title="pendingLostMove?.cardTitle"
        @confirm="handleLostReasonConfirm"
        @cancel="handleLostReasonCancel"
      />

      <!-- Won Reason Modal -->
      <WonReasonModal
        :show="showWonReasonModal"
        :card-title="pendingWonMove?.cardTitle"
        :expected-revenue="pendingWonMove?.expectedRevenue"
        :currency="pendingWonMove?.currency"
        @confirm="handleWonReasonConfirm"
        @cancel="handleWonReasonCancel"
      />

      <!-- Onboarding Tour (B3 - UX/UI Sprint) -->
      <PipelineTour
        :is-active="tour.isActive.value"
        :current-step="tour.currentStep.value"
        :current-step-index="tour.currentStepIndex.value"
        :total-steps="tour.totalSteps.value"
        :progress="tour.progress.value"
        :is-first-step="tour.isFirstStep.value"
        :is-last-step="tour.isLastStep.value"
        :target-rect="tour.targetRect.value"
        @next="tour.nextStep"
        @prev="tour.prevStep"
        @skip="tour.skipTour"
        @complete="tour.completeTour"
      />
    </template>
  </div>
</template>

<style scoped>
.flex-1 {
  min-height: 0;
}

/* ========================================
   PIPELINE BOARD CONTAINER - PADRONIZADO
   Uses design tokens for consistent spacing
   ======================================== */
.pipeline-board-container {
  gap: var(--board-column-gap, 16px); /* PADRONIZADO: gap entre colunas */
}

.pipeline-columns {
  scrollbar-width: thin;
  scrollbar-color: var(--color-woot) var(--color-n-alpha-2, rgba(0, 0, 0, 0.06));

  &::-webkit-scrollbar {
    height: 8px;
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    background: var(--color-n-alpha-2, rgba(0, 0, 0, 0.06));
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-woot);
    border-radius: 4px;
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-n-weak, #e2e8f0);
  border-top: 2px solid var(--color-n-brand, #6366f1);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* ========================================
   COLUMN REORDER - B2.1 UX/UI Sprint
   Drag & Drop styles for reordering stages
   ======================================== */

.pipeline-column-wrapper {
  cursor: grab;
  transition:
    transform var(--animation-duration-normal, 150ms)
      var(--animation-ease-default, ease),
    opacity var(--animation-duration-normal, 150ms)
      var(--animation-ease-default, ease);
  height: 100%;
  /* Design system: w-72 (288px) for pipeline columns */
  width: 288px;
  min-width: 288px;
  max-width: 288px;
  flex-shrink: 0;
  scroll-snap-align: start;
}

.pipeline-column-wrapper:active {
  cursor: grabbing;
}

/* Dragging state - the column being dragged */
.pipeline-column--dragging {
  opacity: 0.5;
  transform: rotate(2deg) scale(1.02);
  z-index: 100;
  box-shadow:
    0 10px 25px -5px rgb(0 0 0 / 0.15),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
}

/* Drag over state - column that can receive the drop */
.pipeline-column--drag-over {
  transform: scale(0.98);
  border-radius: 12px;
  box-shadow:
    inset 0 0 0 3px #3b82f6,
    0 4px 6px -1px rgb(0 0 0 / 0.1);
}

.dark .pipeline-column--drag-over {
  box-shadow:
    inset 0 0 0 3px #60a5fa,
    0 4px 6px -1px rgb(0 0 0 / 0.3);
}

/* Drop indicator animation */
.pipeline-column--drag-over::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(59, 130, 246, 0.1),
    transparent
  );
  animation: dropShimmer 1.5s ease-in-out infinite;
  pointer-events: none;
  border-radius: 12px;
  z-index: 1;
}

@keyframes dropShimmer {
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Respect reduced motion preference */
@media (prefers-reduced-motion: reduce) {
  .pipeline-column-wrapper {
    transition: none;
  }

  .pipeline-column--dragging {
    transform: none;
  }

  .pipeline-column--drag-over {
    transform: none;
  }

  .pipeline-column--drag-over::before {
    animation: none;
  }
}

/* ========================================
   THIN SCROLLBAR - UX Improvements
   Scrollbar fina e discreta para affordance visual
   ======================================== */

.pipeline-board-container {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.3) transparent;
}

.pipeline-board-container::-webkit-scrollbar {
  height: 6px;
}

.pipeline-board-container::-webkit-scrollbar-track {
  background: transparent;
}

.pipeline-board-container::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.3);
  border-radius: 3px;
}

.pipeline-board-container::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.5);
}
</style>
